import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { DatePipe } from '@angular/common';
import { saveAs as importedSaveAs } from 'file-saver';
import { AppMemoryService } from 'app/core/app-memory.service';
import { TranslateService } from '@ngx-translate/core';

interface TableColsType {
  title: string;
  values: {
    key: string[];
    suffix?: string;
    preffix?: string;
    alternative?: any
  }[];
  type?: string;
  className?: string;
  sort?: {
    active: boolean,
    key: string,
    order?: boolean
  };
  translate?: boolean;
}

@Component({
  selector: 'app-report-weight',
  templateUrl: './report-weight.component.html',
  styleUrls: ['./report-weight.component.less']
})
export class ReportWeightComponent implements OnInit {
  @Input() data: any[];
  @Input() title: string;
  @Input() total: number;

  @Output() load = new EventEmitter<String>();
  @Output() sort = new EventEmitter<Object>();

  public tableCols: TableColsType[] = [] as TableColsType[];
  public sortCriteria: { field: string, order: boolean } = {} as { field: string, order: boolean };

  constructor(private datePipe: DatePipe, private appMemory: AppMemoryService, private translateService: TranslateService) {
    this.datePipe = new DatePipe(translateService.currentLang);
  }

  ngOnInit() {
    this.getHeaderItems();
  }

  /**
   * Creates the list of columns
   */
  getHeaderItems() {
    if (this.data && this.data.length) {
      switch (this.title) {
        case 'notes':
          this.tableCols = [
            { title: 'HORSES.horse', type: 'string', values: [
              { key: ['horse', 'name'] }
            ] , sort: { active: true, key: 'horse_id' }, translate: false },
            { title: 'CORE.yard', type: 'string', values: [
              { key: ['horse', 'farm', 'name'] }
            ], translate: false },
            { title: 'HORSES.dateOfNote', type: 'date', values: [
              { key: ['date'], alternative: ['created_at'] }
            ] , sort: { active: false, key: 'date'}, translate: false },
            { title: 'HORSES.noteType', type: 'string', values: [
              { key: ['type'] }
            ] , sort: { active: true, key: 'type' }, translate: true },
            { title: 'CORE.title', type: 'string', values: [
              { key: ['title'] }
            ] , sort: { active: true, key: 'title' }, translate: false },
            { title: 'HORSES.description', type: 'string', values: [
              { key: ['description'] }
            ] , sort: { active: true, key: 'description' }, translate: false },
            { title: 'CORE.attachment', type: 'download', values: [
              { key: ['images'] }
            ] , sort: { active: true, key: 'images' }, translate: false }
          ];
          break;
        case 'vaccinations':
          this.tableCols = [
            { title: 'HORSES.horse', type: 'string', values: [
              { key: ['horse', 'name'] }
            ], translate: false },
            { title: 'CORE.yard', type: 'string', values: [
              { key: ['horse', 'farm', 'name'] }
            ], translate: false },
            { title: 'HORSES.vaccinationName', type: 'string', values: [
              { key: ['name'] }
            ] , sort: { active: true, key: 'name' }, translate: true },
            { title: 'HORSES.vaccinationNumber', type: 'string', values: [
              { key: ['number'] }
            ] , sort: { active: true, key: 'number' }, translate: true },
            { title: 'HORSES.administrationDate', type: 'date', values: [
              { key: ['date'] }
            ] , sort: { active: true, key: 'date' } }
          ];
          break;
        case 'treatments':
          this.tableCols = [
            { title: 'HORSES.horse', type: 'string', values: [
              { key: ['horse', 'name'] }
            ] , sort: { active: true, key: 'horse_id' }, translate: false },
            { title: 'CORE.yard', type: 'string', values: [
              { key: ['horse', 'farm', 'name'] }
            ], translate: false },
            { title: 'HORSES.startDate', type: 'date', values: [
              { key: ['start_date'] }
            ] , sort: { active: true, key: 'start_date' }, translate: false },
            { title: 'HORSES.endDate', type: 'date', values: [
              { key: ['withdrawal'] }
            ] , sort: { active: true, key: 'withdrawal' }, translate: false },
            { title: 'CORE.status', type: 'string', values: [
              { key: ['status'] }
            ] , sort: { active: true, key: 'status' }, translate: true },
            { title: 'TREATS.treatment', type: 'string', values: [
              { key: ['drug_name'] },
              { key: ['reason'], preffix: 'HORSES.reason' }
            ], className: 'block', translate: false },
            { title: 'HORSES.person', type: 'string', values: [
              { key: ['request', 'user', 'name'], alternative: this.appMemory.user.name },
              { key: ['request', 'user', 'last_name'], alternative: this.appMemory.user.last_name }
            ], className: 'inline', translate: false },
            { title: 'HORSES.withdrawal', type: 'string', values: [
              { key: ['duration'], suffix: 'HORSES.days' }
            ], translate: false }
          ];
          break;

        default:
          break;
      }
    }

  }

  /**
   * Find value according to the keys
   * @param {*} list
   * @param {TableColsType} col
   * @returns {({value: (string | number | undefined), suffix: string, preffix: string}[])}
   */
  nestedProperties(list: any, col: TableColsType):
    {value: (string | number | undefined), suffix: string, preffix: string, translate?: boolean}[] {
    const result: {
      value: (string | number | undefined),
      suffix: string, preffix: string,
      translate?: boolean}[] = [] as {value: (string | number | undefined), suffix: string, preffix: string, translate?: boolean}[];

      for (let i = 0; i < col.values.length; i++) {
        let finalValue: any = list;
        const element: { key: string[], suffix?: string, preffix?: string, alternative?: any, translate?: boolean  } = col.values[i];

        for (let j = 0; j < element.key.length; j++) {
          const val = element.key[j];
          if (finalValue != null && finalValue !== undefined) {
            if (!finalValue[val] && element.alternative && Array.isArray(element.alternative)) {
              finalValue = finalValue[element.alternative[0]];
            } else if (!finalValue[val] && element.alternative) {
              finalValue = element.alternative;
            } else {
              finalValue = finalValue[val];
            }
          } else {
            break;
          }
        }
        if (finalValue != null && finalValue !== undefined) {
          if (col.type && col.type === 'date') {
            finalValue = finalValue.split(' ')[0];
            finalValue = this.datePipe.transform(new Date(finalValue), 'dd MMM y').replace(/\./, '');
          } else if (col.type && col.type === 'string') {
            finalValue = finalValue.toString();
          } else {
            finalValue = finalValue;
          }
        }
      result.push({ value: finalValue, suffix: element.suffix, preffix: element.preffix, translate: element.translate });
    }
    return result;
  }

  /**
   * Load more items
   * @param {*} property
   */
  loadMore(property): void {
    this.load.emit(property);
  }

  /**
   * Sort the column by clicked column header
   * @param {*} item
   * @memberof ReportComponent
   */
  sortColumn(item): void {
    if (item && item.active) {
      const column = this.tableCols.find(el => el.sort && el.sort.key === item.key);
      if (column) {
        column.sort.order = !column.sort.order;
      }
      this.sortCriteria.field = item.key;
      this.sortCriteria.order = column.sort.order;
      this.sort.emit({field: this.sortCriteria.field, order: this.sortCriteria.order ? 'asc' : 'desc'});
    }
  }

  downloadAttach(link, fileName: string): void {
    importedSaveAs(link, fileName);
  }
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/emr-webapp/src/app/custom/weight-reporting/components/report-weight/report-weight.component.ts